import { HeroId, HEROES, RosterHeroId } from './heroes';
import { getCurrentMode, saveKeyForMode } from './mode';
import { InfinityStoneId } from './stones';

const LEGACY_KEY = 'avengers-match:save-v1';
const OLD_SW_SAVE_KEY = 'avengers-match:save-sw-v1';
const CURRENT_VERSION = 3;

function keyFor(): string {
  return saveKeyForMode(getCurrentMode());
}

export type StarCount = 0 | 1 | 2 | 3;

export type SaveV3 = {
  version: 3;
  starsByLevelId: Record<string, StarCount>;
  unlockedHeroes: RosterHeroId[];
  lastTeam: HeroId[];
  tutorialSeen: Record<string, boolean>;
  failureCountsByLevelId: Record<string, number>;
  audioMuted: boolean;
  stonesEarned: InfinityStoneId[];
  vibraniumEarned?: number;
  morphinGridEarned?: number;
  lastViewedWorld?: number;
  equippedStone?: InfinityStoneId;
  powerRangersUnlocked?: boolean;
};

export const VIBRANIUM_PER_BOSS = 5;

export type SaveV2 = SaveV3;

function defaultSave(): SaveV3 {
  const mode = getCurrentMode();
  const allStarters: RosterHeroId[] =
    mode === 'powerrangers'
      ? ['red-ranger']
      : ['captain-america', 'black-panther', 'iron-man', 'spider-man'];
  const defaultTeam =
    mode === 'powerrangers'
      ? (['red-ranger'] as HeroId[])
      : (['captain-america', 'black-panther', 'iron-man'] as HeroId[]);
  return {
    version: 3,
    starsByLevelId: {},
    unlockedHeroes: allStarters,
    lastTeam: defaultTeam,
    tutorialSeen: {},
    failureCountsByLevelId: {},
    audioMuted: false,
    stonesEarned: [],
  };
}

type LegacySave = Partial<SaveV3> & {
  version: number;
  starwarsUnlocked?: boolean;
  kyberEarned?: number;
};

export function loadSave(): SaveV3 {
  try {
    const key = keyFor();
    let raw = localStorage.getItem(key);
    if (!raw && key === 'avengers-match:save-v1') {
      raw = localStorage.getItem(LEGACY_KEY);
    }
    if (!raw) return defaultSave();
    const parsed = JSON.parse(raw) as LegacySave;
    const v = parsed.version as number;
    if (v !== CURRENT_VERSION) {
      const fresh = defaultSave();
      saveSave(fresh);
      return fresh;
    }
    const base = defaultSave();
    const prUnlockFromLegacy =
      parsed.powerRangersUnlocked ??
      parsed.starwarsUnlocked ??
      ((parsed.starsByLevelId ?? {})['w14-boss'] ?? 0) > 0;
    const morphinFromLegacy =
      parsed.morphinGridEarned ?? parsed.kyberEarned ?? 0;
    return {
      ...base,
      ...parsed,
      version: CURRENT_VERSION,
      starsByLevelId: { ...(parsed.starsByLevelId ?? {}) },
      unlockedHeroes: filterKnownRosterHeroes(
        parsed.unlockedHeroes ?? base.unlockedHeroes,
      ),
      lastTeam: filterKnownHeroes(parsed.lastTeam ?? base.lastTeam),
      tutorialSeen: { ...(parsed.tutorialSeen ?? {}) },
      failureCountsByLevelId: { ...(parsed.failureCountsByLevelId ?? {}) },
      audioMuted: parsed.audioMuted ?? base.audioMuted,
      stonesEarned: (parsed.stonesEarned ?? []).filter(isKnownStone),
      vibraniumEarned:
        parsed.vibraniumEarned ??
        Math.floor(
          (Object.values(parsed.starsByLevelId ?? {}) as number[]).reduce(
            (a, b) => a + b,
            0,
          ) / 5,
        ),
      morphinGridEarned: morphinFromLegacy,
      lastViewedWorld: parsed.lastViewedWorld,
      equippedStone: parsed.equippedStone && isKnownStone(parsed.equippedStone)
        ? parsed.equippedStone
        : undefined,
      powerRangersUnlocked: prUnlockFromLegacy,
    };
  } catch {
    return defaultSave();
  }
}

export function cleanupLegacyStorage(): void {
  try {
    localStorage.removeItem(OLD_SW_SAVE_KEY);
  } catch {}
}

function filterKnownHeroes(ids: HeroId[]): HeroId[] {
  return ids.filter((id) => id in HEROES);
}

function filterKnownRosterHeroes(ids: RosterHeroId[]): RosterHeroId[] {
  return ids.filter((id) => id in HEROES);
}

function isKnownStone(id: InfinityStoneId | string): id is InfinityStoneId {
  const known: string[] = [
    'power',
    'mind',
    'reality',
    'space',
    'time',
    'soul',
    'morphin-blast',
    'zord-strike',
  ];
  return known.includes(id as string);
}

export function saveSave(s: SaveV3): void {
  localStorage.setItem(keyFor(), JSON.stringify(s));
}

export function recordStars(levelId: string, stars: StarCount): SaveV3 {
  const s = loadSave();
  const prev = s.starsByLevelId[levelId] ?? 0;
  if (stars > prev) s.starsByLevelId[levelId] = stars;
  saveSave(s);
  return s;
}

export function recordTeam(team: HeroId[]): SaveV3 {
  const s = loadSave();
  s.lastTeam = team.slice(0, 3);
  saveSave(s);
  return s;
}

export function markTutorialSeen(key: string): SaveV3 {
  const s = loadSave();
  s.tutorialSeen[key] = true;
  saveSave(s);
  return s;
}

export function recordFailure(levelId: string): SaveV3 {
  const s = loadSave();
  s.failureCountsByLevelId[levelId] = (s.failureCountsByLevelId[levelId] ?? 0) + 1;
  saveSave(s);
  return s;
}

export function clearFailures(levelId: string): SaveV3 {
  const s = loadSave();
  if (s.failureCountsByLevelId[levelId]) {
    delete s.failureCountsByLevelId[levelId];
    saveSave(s);
  }
  return s;
}

export function setMuted(muted: boolean): SaveV3 {
  const s = loadSave();
  s.audioMuted = muted;
  saveSave(s);
  return s;
}

export function resetSave(): SaveV3 {
  localStorage.removeItem(keyFor());
  const fresh = defaultSave();
  saveSave(fresh);
  return fresh;
}

export function unlockPowerRangers(): void {
  const s = loadSave();
  s.powerRangersUnlocked = true;
  saveSave(s);
}

export function isPowerRangersUnlocked(): boolean {
  const raw = localStorage.getItem('avengers-match:save-v1');
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw) as {
      powerRangersUnlocked?: boolean;
      starwarsUnlocked?: boolean;
    };
    return !!(parsed.powerRangersUnlocked || parsed.starwarsUnlocked);
  } catch {
    return false;
  }
}


export function setLastViewedWorld(world: number): SaveV3 {
  const s = loadSave();
  s.lastViewedWorld = world;
  saveSave(s);
  return s;
}

export function setEquippedStone(id: InfinityStoneId | undefined): SaveV3 {
  const s = loadSave();
  s.equippedStone = id;
  saveSave(s);
  return s;
}

export function resolveEquippedStone(save: SaveV3): InfinityStoneId | undefined {
  if (save.equippedStone && save.stonesEarned.includes(save.equippedStone)) {
    return save.equippedStone;
  }
  return save.stonesEarned[0];
}

export function failuresFor(save: SaveV3, levelId: string): number {
  return save.failureCountsByLevelId[levelId] ?? 0;
}

export function starsFor(save: SaveV3, levelId: string): StarCount {
  return save.starsByLevelId[levelId] ?? 0;
}

export function unlockHero(id: RosterHeroId): SaveV3 {
  const s = loadSave();
  if (!s.unlockedHeroes.includes(id)) {
    s.unlockedHeroes.push(id);
    saveSave(s);
  }
  return s;
}

export function grantStone(id: InfinityStoneId): SaveV3 {
  const s = loadSave();
  if (!s.stonesEarned.includes(id)) {
    s.stonesEarned.push(id);
    saveSave(s);
  }
  return s;
}

export function grantVibranium(amount: number): SaveV3 {
  const s = loadSave();
  s.vibraniumEarned = (s.vibraniumEarned ?? 0) + Math.max(0, amount);
  saveSave(s);
  return s;
}

export function totalStars(save: SaveV3): number {
  return (Object.values(save.starsByLevelId) as number[]).reduce((a, b) => a + b, 0);
}

export function vibraniumCount(save: SaveV3): number {
  return save.vibraniumEarned ?? 0;
}

export function grantMorphinGrid(amount: number): SaveV3 {
  const s = loadSave();
  s.morphinGridEarned = (s.morphinGridEarned ?? 0) + Math.max(0, amount);
  saveSave(s);
  return s;
}

export function morphinGridCount(save: SaveV3): number {
  return save.morphinGridEarned ?? 0;
}

export const MORPHIN_GRID_TIERS: { threshold: number; name: string; description: string }[] = [
  { threshold: 5, name: 'Morphin Focus', description: 'Heroes start with 2 charges' },
  { threshold: 10, name: 'Grid Resonance', description: '+2 max HP for all heroes' },
  { threshold: 15, name: 'Ranger Shield', description: 'First enemy attack blocked' },
  { threshold: 20, name: 'Zord Bond', description: 'Team-up bonus 1.5× → 1.7×' },
  { threshold: 25, name: 'Power Coin Charge', description: '+1 damage per match' },
  { threshold: 30, name: 'Morphin Mastery', description: 'Enemies start with -10% HP' },
];

export type VibraniumBuffs = {
  startCharges: number;
  heroMaxHpBonus: number;
  firstAttackFree: boolean;
  teamUpMultiplier: number;
  matchBonusDamage: number;
  enemyHpMult: number;
};

export const VIBRANIUM_TIERS: {
  threshold: number;
  name: string;
  description: string;
}[] = [
  { threshold: 5, name: 'Wakandan Shields', description: 'Heroes start with 2 charges' },
  { threshold: 10, name: 'Vibranium Heart', description: '+2 max HP for all heroes' },
  { threshold: 15, name: 'Reinforced Armor', description: 'First enemy attack blocked' },
  { threshold: 20, name: 'Kimoyo Beads', description: 'Team-up bonus 1.5× → 1.7×' },
  { threshold: 25, name: "Panther's Blessing", description: '+1 damage per match' },
  { threshold: 30, name: 'Sokovia Tech', description: 'Enemies start with -10% HP' },
];

export function vibraniumBuffs(save: SaveV3): VibraniumBuffs {
  const mode = getCurrentMode();
  const v = mode === 'powerrangers' ? morphinGridCount(save) : vibraniumCount(save);
  return {
    startCharges: v >= 5 ? 2 : 0,
    heroMaxHpBonus: v >= 10 ? 2 : 0,
    firstAttackFree: v >= 15,
    teamUpMultiplier: v >= 20 ? 1.7 : 1.5,
    matchBonusDamage: v >= 25 ? 1 : 0,
    enemyHpMult: v >= 30 ? 0.9 : 1,
  };
}

export function powerUpHpScale(save: SaveV3): number {
  const v = vibraniumCount(save);
  const s = save.stonesEarned.length;
  const raw = 1 + v * 0.03 + s * 0.2;
  return Math.min(3.5, raw);
}
